// /c/:slug — the campaign brief that replaces the Google Doc guides. One
// centered column you scroll straight down: the creator's photo, pool bar,
// title, facts and Join, then short sections. No tabs, no boxes and no grey
// text; captions aren't listed because the AI writes them.
// "What to clip" is the streams the launcher picked: "Clip with AI" opens a
// normal Clipbait run (/home?clip=…&campaign=…) for signed-in users, and
// sends guests through signup straight back into that run (the paywall and
// trial apply there as usual).
// The Submit section is the in-app submission panel (SubmitClip.jsx).
// "What's working" ranks the campaign's accepted clips by results (leads or
// sales from the Clipbait pixel, or views) and refreshes on every visit.
// Leads/sales campaigns also show joined clippers their tracked links and
// show the owner the pixel snippet (routes/campaignPixel.js). Clippers can
// also connect Instagram or a Facebook Page so anyone commenting the trigger
// word gets a DM with their own tracked link (Clipbait DM, services/clipbaitDm.js).
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiCheck, FiCheckCircle, FiDollarSign, FiDownload, FiExternalLink, FiFolder, FiLink, FiVideo, FiX, FiZap } from "react-icons/fi";
import PhotoLightbox from "../../components/PhotoLightbox/PhotoLightbox";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import {
  CampaignsShell, PLATFORM_META, PLATFORM_POST_LABEL,
  budgetLeft, copyText, fmtMoney,
  StreamImage, cleanStreamTitle, isDriveFolder, isDriveUrl, isPool, payRate,
} from "./campaignsShared";
import { SubmitPanel, fmtNum } from "./SubmitClip";

const MAX_STREAMS = 6;

function fmtDuration(sec) {
  const s = Number(sec) || 0;
  if (!s) return "";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

function Rule({ no, children }) {
  return (
    <li className={`cmp-rule${no ? " no" : ""}`}>
      <span className="cmp-rule-icon" aria-hidden="true">{no ? <FiX /> : <FiCheck />}</span>
      <span>{children}</span>
    </li>
  );
}

const slugName = (x) => String(x || "streamer").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Copyable({ text }) {
  if (!text) return null;
  return (
    <button type="button" className="cmp-copyable" onClick={() => copyText(text, "Copied")}>
      {text}<span className="cmp-copyable-tag">Copy</span>
    </button>
  );
}

// Big pool-left bar under the photo; fills in on load.
function PoolBar({ left, total, pct }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setShown(pct), 80);
    return () => clearTimeout(t);
  }, [pct]);
  if (!total) return null;
  return (
    <div className="cmp-pool">
      <div className="cmp-pool-head"><strong>{fmtMoney(left)}</strong><span>left of {fmtMoney(total)}</span></div>
      <div className="cmp-pool-bar"><span style={{ width: `${shown}%` }} /></div>
    </div>
  );
}

// Username ideas in the streamer's name: handles are one per person, so
// clippers pick (or riff on) one instead of copying an exact name.
function nameIdeas(name, picked) {
  const base = String(name || "").replace(/[^a-z0-9]+/gi, "");
  if (!base) return picked ? [picked] : [];
  const ideas = ["Clips", "Daily", "Moments", "Reels"].map((w) => `${base}${w}`);
  const mine = String(picked || "").replace(/^@/, "");
  return mine && !ideas.some((x) => x.toLowerCase() === mine.toLowerCase().replace(/[^a-z0-9]/g, "")) ? [mine, ...ideas] : ideas;
}

function PlatformIcons({ platforms, size = 20 }) {
  return (
    <span className="cmp-icons">
      {(platforms || []).map((p) => <span key={p} title={PLATFORM_POST_LABEL[p]}>{PLATFORM_META[p]?.icon(size)}</span>)}
    </span>
  );
}

export default function CampaignBriefPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [sources, setSources] = useState(null);
  const [joined, setJoined] = useState(false);
  const [working, setWorking] = useState(null);
  const [myLinks, setMyLinks] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [dm, setDm] = useState(null);

  useEffect(() => {
    let off = false;
    setC(null);
    setError("");
    axiosInstance.get(`/campaigns/${encodeURIComponent(slug)}`)
      .then((r) => { if (!off) setC(r.data); })
      .catch((e) => { if (!off) setError(e?.response?.status === 404 ? "This campaign doesn't exist or isn't live yet." : "Couldn't load this campaign."); });
    axiosInstance.get(`/campaigns/${encodeURIComponent(slug)}/sources`)
      .then((r) => { if (!off) setSources(r.data); })
      .catch(() => { if (!off) setSources({ sources: [], vods: [] }); });
    return () => { off = true; };
  }, [slug]);

  useEffect(() => {
    if (!user || !c?._id) return;
    axiosInstance.get("/campaigns/applied")
      .then((r) => setJoined((r.data.applications || []).some((a) => String(a.campaign?._id) === String(c._id))))
      .catch(() => {});
  }, [user, c?._id]);

  const wantsResults = c && c.objective && c.objective !== "views";
  const owner = !!(user && c && (String(user._id) === String(c.createdBy) || user.isAdmin));

  useEffect(() => {
    if (!c?._id) return;
    axiosInstance.get(`/px/working/${c._id}`).then((r) => setWorking(r.data)).catch(() => setWorking(null));
  }, [c?._id]);

  useEffect(() => {
    if (!c?._id || !wantsResults || !user) return;
    if (joined) {
      axiosInstance.get(`/px/links/${c._id}`).then((r) => setMyLinks(r.data)).catch(() => {});
      axiosInstance.get("/dm/status").then((r) => setDm(r.data)).catch(() => {});
    }
    if (owner) axiosInstance.get(`/px/setup/${c._id}`).then((r) => setTracking(r.data)).catch(() => {});
  }, [c?._id, wantsResults, user, joined, owner]);

  // Clipbait DM: connect with the comment + messaging permissions, then come back here.
  const connectDm = (platform) => {
    const path = platform === "instagram" ? "/auth/instagram" : "/auth/social/facebook";
    const qs = new URLSearchParams({ mode: "dm", returnTo: `/c/${slug}#my-links`, token: localStorage.getItem("token") || "" });
    window.location.href = `${axiosInstance.defaults.baseURL}${path}?${qs}`;
  };

  // Guests sign up and land back where they were heading.
  const signupThen = (path) => navigate(`/signup?returnUrl=${encodeURIComponent(path)}`);

  // Joining puts the campaign on their Dashboard ("Clipping for").
  const join = async () => {
    if (!user) return signupThen(`/c/${slug}`);
    try {
      await axiosInstance.post(`/campaigns/${c._id}/apply`, {});
    } catch (e) {
      if (e?.response?.status !== 409) return toast.error("Couldn't join. Try again.");
    }
    setJoined(true);
    toast.success("Joined. It's on your dashboard.");
  };

  const clipWithAI = (url) => {
    const qs = new URLSearchParams({ clip: url, campaign: c._id });
    if (!(sources?.channel || c.channel)?.platform) qs.set("noChannel", "1");
    if (!user) return signupThen(`/home?${qs}`);
    const mine = String(user._id) === String(c.createdBy);
    if (!joined && !mine) axiosInstance.post(`/campaigns/${c._id}/apply`, {}).catch(() => {});
    navigate(`/home?${qs}`);
  };

  if (error) {
    return (
      <CampaignsShell>
        <div className="cmp-empty">
          <h2>Campaign not found</h2>
          <p>{error}</p>
          <button className="cmp-btn" onClick={() => navigate("/campaigns")}>Browse campaigns</button>
        </div>
      </CampaignsShell>
    );
  }
  if (!c) return <CampaignsShell><div className="cmp-loading">Loading brief…</div></CampaignsShell>;

  const b = c.brief || {};
  const ck = b.checklist || {};
  const ps = b.pageSetup || {};
  const aud = b.audience || {};
  const { total, left, pct } = budgetLeft(c);
  const name = c.streamerName || c.brandName;
  const isOwner = user && String(user._id) === String(c.createdBy);
  const cpm = Number(c.payoutPer1K) || 0;
  const { text: cpmText, unit: payUnitText } = payRate(c);
  const feePct = Math.round((Number(c.payoutFeeRate) || 0) * 100);
  const downloadPhoto = async () => {
    try {
      const r = await axiosInstance.get(`/campaigns/${c._id}/photo`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slugName(name)}-photo.${(r.data.type.split("/")[1] || "jpg").replace("jpeg", "jpg")}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Couldn't download the photo");
    }
  };
  const avatar = c.brandLogo ? <img src={c.brandLogo} alt="" /> : <span>{name?.[0]?.toUpperCase()}</span>;
  const streams = [
    ...(sources?.vods || []).map((v) => ({
      key: v.id, url: v.url, title: v.title, thumb: v.thumbnail,
      meta: [fmtDuration(v.durationSec), v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ""].filter(Boolean).join(" · "),
    })),
    ...(sources?.sources || []).map((s, i) => ({ key: `s${i}`, url: s.url, title: s.label || s.url, thumb: s.thumbnail, meta: "" })),
  ].slice(0, MAX_STREAMS);
  const legacyReqs = String(c.requirements || "").split("\n").map((x) => x.replace(/^[-•*]\s*/, "").trim()).filter(Boolean);
  // Examples to copy or riff on. Older briefs only have one name and bio.
  const pageNames = ps.nameExamples?.length ? ps.nameExamples : (ps.displayName || ps.username) ? nameIdeas(name, ps.username) : [];
  const bioLines = ps.bioExamples?.length ? ps.bioExamples : [ps.bioDisclaimer].filter(Boolean);
  const hasSetup = !!(pageNames.length || bioLines.length || ps.bioLink);

  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <CampaignsShell wide>
      <div className="cmp-brief">
        {/* Creator, headline, facts, Join */}
        <header className="cmp-hero">
          {c.bannerImage
            ? <img className="cmp-hero-banner" src={c.bannerImage} alt="" />
            : <div className="cmp-hero-photo">{avatar}</div>}
          {c.funding?.testMode && <span className="cmp-pill">Test</span>}
          {c.status === "active"
            ? <span className="cmp-live"><i aria-hidden="true" />Live</span>
            : <span className={`cmp-pill ${c.status}`}>{c.status === "pending_funding" ? "Awaiting funding" : c.status}</span>}
          {isPool(c) ? (
            <div className="cmp-pool">
              <div className="cmp-pool-head"><strong>{fmtMoney(total)}</strong><span>pool{c.status === "active" && c.expiresAt ? ` · ends ${new Date(c.expiresAt).toLocaleDateString()}` : ""}</span></div>
            </div>
          ) : <PoolBar left={left} total={total} pct={pct} />}
          <h1 className="cmp-hero-title">{c.streamerName ? `${name} clipping campaign` : c.title}</h1>
          <dl className="cmp-facts">
            <div><dt>Pay</dt>{isPool(c) ? <dd>Share<small> of views</small></dd> : <dd>{cpmText}<small>/{payUnitText}</small></dd>}</div>
            <div><dt>Views</dt><dd>{aud.usOnly ? "US only" : "All"}</dd></div>
            <div><dt>Post on</dt><dd><PlatformIcons platforms={c.platforms} size={22} /></dd></div>
          </dl>
          <div className="cmp-hero-actions">
            {!isOwner && c.status === "active" && (
              joined
                ? <button className="cmp-btn big" onClick={() => goTo("submit")}>Submit clip</button>
                : <button className="cmp-btn big" onClick={join}>Join campaign</button>
            )}
            {streams.length > 0 && <button className={`cmp-btn big${isOwner ? "" : " ghost"}`} onClick={() => goTo("clip")}>Clip with AI</button>}
            {isOwner && <button className="cmp-btn big ghost" onClick={() => navigate(`/campaigns/launch?edit=${c._id}`)}>Edit</button>}
          </div>
        </header>

        {/* The brief, top to bottom */}
        <section id="rules" className="cmp-sec">
          <h2>Rules</h2>
          <ul className="cmp-rules">
            {c.platforms?.length > 0 && <Rule>Post on <PlatformIcons platforms={c.platforms} size={18} /></Rule>}
            <Rule>{aud.usOnly ? "US views only" : "All views count"}</Rule>
            {isPool(c)
              ? <Rule>Keep posts up until the pool ends</Rule>
              : aud.keepUpDays > 0 && <Rule>Keep posts up {aud.keepUpDays} days</Rule>}
            {ck.hashtags?.length > 0 && <Rule>Hashtags: <span className="cmp-hot">{ck.hashtags.slice(0, 3).join(" ")}</span></Rule>}
            {legacyReqs.map((x, i) => <Rule key={`r${i}`}>{x}</Rule>)}
            {ck.dos?.map((x, i) => <Rule key={`d${i}`}>{x.replace(/burned-in captions/i, "captions")}</Rule>)}
            {ck.donts?.map((x, i) => <Rule key={`n${i}`} no>{x}</Rule>)}
          </ul>
        </section>

        {streams.length > 0 && (
          <section id="clip" className="cmp-sec">
            <h2>What to clip</h2>
            <div className="cmp-streams">
              {streams.map((s) => (
                <div key={s.key} className="cmp-stream">
                  {isDriveFolder(s.url) ? (
                    <a className="cmp-stream-thumb" href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${s.title}`}>
                      <StreamImage url={s.url} placeholderClass="cmp-stream-ph" />
                      <span className="cmp-stream-cta"><FiFolder aria-hidden="true" /> Open folder</span>
                    </a>
                  ) : (
                    <button type="button" className="cmp-stream-thumb" onClick={() => clipWithAI(s.url)} aria-label={`Clip ${s.title} with AI`}>
                      <StreamImage url={s.url} thumb={s.thumb} placeholderClass="cmp-stream-ph" />
                      <span className="cmp-stream-cta"><FiZap aria-hidden="true" /> Clip with AI</span>
                    </button>
                  )}
                  <span className="cmp-stream-title">{isDriveUrl(s.url) ? cleanStreamTitle(s.title) : s.title}</span>
                  <span className="cmp-stream-row">
                    {s.meta && <span className="cmp-stream-meta">{s.meta}</span>}
                    {/* For clippers who'd rather cut it themselves */}
                    <a className="cmp-stream-manual" href={s.url} target="_blank" rel="noopener noreferrer">Clip manually <FiExternalLink aria-hidden="true" /></a>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {working?.clips?.length > 0 && (
          <section id="working" className="cmp-sec">
            <h2>What's working</h2>
            <ol className="cmp-working">
              {working.clips.map((r, i) => {
                const sum = (k) => r[k] + (r.bio?.[k] || 0);
                return (
                  <li key={r.id}>
                    <span className="cmp-working-rank">{i + 1}</span>
                    <span className="cmp-working-icon">{PLATFORM_META[r.platform]?.icon(22)}</span>
                    <a className="cmp-working-who" href={r.postUrl} target="_blank" rel="noopener noreferrer">{r.username ? `@${r.username}` : "Watch clip"} <FiExternalLink aria-hidden="true" /></a>
                    <span className="cmp-working-stats">
                      <span><strong>{fmtNum(r.views)}</strong> views</span>
                      {r.comments > 0 && <span><strong>{fmtNum(r.comments)}</strong> comments</span>}
                      {r.comments > 0 && <span><strong>{fmtNum(r.dms)}</strong> DMs</span>}
                      {working.objective !== "views" && <span><strong>{fmtNum(sum("clicks"))}</strong> clicks</span>}
                      {working.objective !== "views" && <span className={working.objective === "leads" ? "hot" : ""}><strong>{fmtNum(sum("leads"))}</strong> leads</span>}
                      {working.objective === "sales" && <span className="hot"><strong>{fmtNum(sum("sales"))}</strong> sales</span>}
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {owner && wantsResults && tracking && (
          <section id="tracking" className="cmp-sec">
            <h2>Tracking</h2>
            <p className={`cmp-px-status${tracking.seenAt ? " on" : ""}`}>
              <i aria-hidden="true" />{tracking.seenAt ? `Pixel installed · last seen ${new Date(tracking.seenAt).toLocaleString()}` : "Pixel not installed yet"}
            </p>
            <ol className="cmp-steps">
              <li><span className="cmp-step-n">1</span><div><strong>Add this to every page of your site</strong><Copyable text={tracking.snippet} /></div></li>
              <li><span className="cmp-step-n">2</span><div><strong>{c.objective === "sales" ? "Call this when someone buys" : "Call this when someone signs up"}</strong><Copyable text={c.objective === "sales" ? tracking.saleCall : tracking.leadCall} /></div></li>
            </ol>
            <dl className="cmp-facts cmp-px-totals">
              <div><dt>Clicks</dt><dd>{fmtNum(tracking.totals.clicks)}</dd></div>
              <div><dt>Leads</dt><dd>{fmtNum(tracking.totals.leads)}</dd></div>
              {c.objective === "sales" && <div><dt>Sales</dt><dd>{fmtNum(tracking.totals.sales)}</dd></div>}
              {c.objective === "sales" && <div><dt>Revenue</dt><dd>{fmtMoney(tracking.totals.revenue)}</dd></div>}
            </dl>
          </section>
        )}

        {hasSetup && (
          <section id="setup" className="cmp-sec">
            <h2>Page setup</h2>
            <ol className="cmp-steps">
              <li><span className="cmp-step-n">1</span><div><strong>Make a fresh account</strong><PlatformIcons platforms={c.platforms} /></div></li>
              {pageNames.length > 0 && (
                <li>
                  <span className="cmp-step-n">2</span>
                  <div>
                    <strong>Use a name like</strong>
                    <span className="cmp-ideas">{pageNames.map((x) => <Copyable key={x} text={x} />)}</span>
                  </div>
                </li>
              )}
              {(bioLines.length > 0 || ps.bioLink) && (
                <li><span className="cmp-step-n">{pageNames.length ? 3 : 2}</span><div><strong>Bio like</strong><span className="cmp-copyables">{bioLines.map((x) => <Copyable key={x} text={x} />)}<Copyable text={ps.bioLink} /></span></div></li>
              )}
              <li>
                <span className="cmp-step-n">{2 + !!pageNames.length + !!(bioLines.length || ps.bioLink)}</span>
                <div>
                  <strong>Use {name}'s photo</strong>
                  <span className="cmp-step-photo">
                    <button type="button" className="cmp-step-avatar" disabled={!c.brandLogo} onClick={() => setPhotoOpen(true)} aria-label={`Enlarge ${name}'s photo`}>{avatar}</button>
                    {c.brandLogo && <button type="button" className="cmp-copyable" onClick={downloadPhoto}><FiDownload aria-hidden="true" /> Download</button>}
                  </span>
                  {photoOpen && <PhotoLightbox src={c.brandLogo} name={name} onClose={() => setPhotoOpen(false)} onDownload={downloadPhoto} />}
                </div>
              </li>
            </ol>
          </section>
        )}

        {myLinks?.bio && (
          <section id="my-links" className="cmp-sec">
            <h2>Your links</h2>
            <ol className="cmp-steps">
              <li>
                <span className="cmp-step-n">1</span>
                <div>
                  <strong>Put this in your bio</strong>
                  <Copyable text={myLinks.bio.url} />
                </div>
              </li>
              {ck.manychatKeyword && dm?.enabled && (
                <li>
                  <span className="cmp-step-n">2</span>
                  <div>
                    <strong>Auto-DM anyone who comments <span className="cmp-hot">{ck.manychatKeyword}</span></strong>
                    <span className="cmp-dm-row">
                      {[["instagram", "Instagram", dm.instagram], ["facebook", "Facebook", dm.facebook]].map(([p, label, st]) => (
                        st?.dm
                          ? <span key={p} className="cmp-dm-on">{PLATFORM_META[p]?.icon(18)}{st.username ? `@${st.username}` : st.name || label} · on</span>
                          : <button key={p} type="button" className="cmp-btn ghost" onClick={() => connectDm(p)}>{PLATFORM_META[p]?.icon(18)}Connect {label}</button>
                      ))}
                    </span>
                  </div>
                </li>
              )}
              {myLinks.clips.length > 0 && (
                <li>
                  <span className="cmp-step-n">{ck.manychatKeyword && dm?.enabled ? 3 : 2}</span>
                  <div>
                    <strong>Or one link per clip</strong>
                    {myLinks.clips.map((l) => (
                      <span key={l.submission} className="cmp-link-row">
                        <span className="cmp-link-who">{PLATFORM_META[l.platform]?.icon(16)}{l.username ? `@${l.username}` : "Clip"}</span>
                        <Copyable text={l.url} />
                      </span>
                    ))}
                  </div>
                </li>
              )}
            </ol>
          </section>
        )}

        <section id="pay" className="cmp-sec">
          <h2>Get paid</h2>
          <div className="cmp-flowrow">
            <div><span aria-hidden="true"><FiCheckCircle /></span>Join</div>
            <div><span aria-hidden="true"><FiVideo /></span>Post</div>
            <button type="button" onClick={() => goTo("submit")}><span aria-hidden="true"><FiLink /></span>Submit</button>
            <div className="hot">
              <span aria-hidden="true"><FiDollarSign /></span>
              {isPool(c) ? (
                <>10% of views = {fmtMoney(total * 0.1)}<small>before {feePct}% fee · no cap · paid 3 days after the pool closes</small></>
              ) : (
                <>
                  {cpmText} per {payUnitText} views
                  {c.pay?.maxPayout > 0 && <small>up to {fmtMoney(c.pay.maxPayout)} a clip</small>}
                  {feePct > 0 && <small>before {feePct}% fee</small>}
                </>
              )}
            </div>
          </div>
        </section>

        {b.endCard?.url && (
          <section id="endcard" className="cmp-sec">
            <h2>End card</h2>
            <div className="cmp-endcard">
              {/\.(png|jpe?g|gif|webp)(\?|$)/i.test(b.endCard.url) && <img src={b.endCard.url} alt="End card" />}
              {/\.(mp4|mov|webm)(\?|$)/i.test(b.endCard.url) && <video src={b.endCard.url} controls muted playsInline />}
              <a className="cmp-btn ghost" href={b.endCard.url} target="_blank" rel="noopener noreferrer" download={b.endCard.name || true}>Download end card</a>
            </div>
          </section>
        )}

        {c.campaignBrief && (
          <section id="guide" className="cmp-sec">
            <h2>Guide</h2>
            <p className="cmp-guide">{c.campaignBrief}</p>
          </section>
        )}

        <section id="submit" className="cmp-sec">
          <h2>Submit</h2>
          <div className="cmp-submit">
            {c.submitSteps?.length > 0 && (
              <ol className="cmp-steps">{c.submitSteps.map((x, i) => <li key={i}><span className="cmp-step-n">{i + 1}</span><div><strong>{x}</strong></div></li>)}</ol>
            )}
            {isOwner ? (
              <button className="cmp-btn big" onClick={() => navigate("/campaigns/dashboard")}>Review submissions</button>
            ) : user ? (
              <SubmitPanel campaign={c} onJoined={() => setJoined(true)} />
            ) : (
              <button className="cmp-btn big" onClick={() => signupThen(`/c/${slug}`)}>Sign up to submit</button>
            )}
          </div>
        </section>
      </div>
    </CampaignsShell>
  );
}
